import { useEffect, useState } from 'react'
import { useAsync } from '../hooks/useAsync'
import { useSession } from '../context/SessionContext'
import { startDiscordLogin } from '../services/auth'
import { errorMessage } from '../services/api'
import { fetchPoll, voteOnPoll } from '../services/league'
import { Notice } from './ui'
import type { Poll } from '../types'
import './HomePoll.css'

function formatClosesAt(iso: string, isOpen: boolean): string {
  const at = new Date(iso)
  const when = at.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
  return isOpen ? `Open until ${when}` : `Closed ${when}`
}

function voteLabel(count: number): string {
  return `${count} ${count === 1 ? 'vote' : 'votes'}`
}

/**
 * Homepage poll, sat under the hero actions.
 *
 * @remarks
 * Counts are public and anonymous. The only personal fact shown is whether
 * *you* already voted, never who else chose which option.
 */
export default function HomePoll() {
  const { user } = useSession()
  const loaded = useAsync(fetchPoll, [user?.id])
  const [applied, setApplied] = useState<Poll | null | undefined>(undefined)
  const [pendingId, setPendingId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setApplied(undefined)
  }, [user?.id])

  const poll = applied === undefined ? loaded.data : applied

  async function vote(optionId: number): Promise<void> {
    setPendingId(optionId)
    setError(null)
    try {
      setApplied(await voteOnPoll(optionId))
    } catch (caught) {
      setError(errorMessage(caught))
    } finally {
      setPendingId(null)
    }
  }

  if (loaded.loading && applied === undefined) {
    return null
  }
  if (!poll) {
    if (loaded.error) {
      return <Notice tone="error">{loaded.error}</Notice>
    }
    return null
  }

  const canVote = Boolean(user) && poll.isOpen && poll.myOptionId === null

  return (
    <div className="home-poll">
      <div className="home-poll__header">
        <h2 className="home-poll__question">{poll.question}</h2>
        <p className="home-poll__meta muted">
          {formatClosesAt(poll.closesAt, poll.isOpen)}
          {poll.totalVotes > 0 ? ` · ${voteLabel(poll.totalVotes)}` : ''}
        </p>
      </div>

      <ul className="home-poll__options">
        {poll.options.map(option => {
          const mine = poll.myOptionId === option.id
          const share = poll.totalVotes === 0 ? 0 : option.votes / poll.totalVotes
          const row = (
            <>
              <span
                className="home-poll__bar"
                style={{ width: `${Math.round(share * 100)}%` }}
                aria-hidden="true"
              />
              <span className="home-poll__label">
                {option.label}
                {mine ? <span className="home-poll__yours">Your vote</span> : null}
              </span>
              <span className="home-poll__count numeric">{option.votes}</span>
            </>
          )

          if (canVote) {
            return (
              <li key={option.id}>
                <button
                  type="button"
                  className="home-poll__option"
                  disabled={pendingId !== null}
                  onClick={() => void vote(option.id)}
                >
                  {row}
                </button>
              </li>
            )
          }

          return (
            <li key={option.id}>
              <div className={`home-poll__option${mine ? ' home-poll__option--mine' : ''}`}>
                {row}
              </div>
            </li>
          )
        })}
      </ul>

      {error && <Notice tone="error">{error}</Notice>}
      {loaded.error && !error && <Notice tone="error">{loaded.error}</Notice>}

      {!user && poll.isOpen && (
        <button type="button" className="btn btn--sm" onClick={() => startDiscordLogin('home')}>
          Sign in to vote
        </button>
      )}
      {user && poll.isOpen && poll.myOptionId !== null && (
        <p className="muted home-poll__hint">Thanks — one vote per person.</p>
      )}
    </div>
  )
}
